import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
// Import icons from react-icons
import { 
  SiSlack, 
  SiDiscord, 
  SiGmail, 
  SiGithub, 
  SiSalesforce, 
  SiJira,
  SiAmazon
} from "react-icons/si";
import { FaCode, FaRobot, FaUserTie, FaLock, FaLockOpen, FaCheck, FaArrowRight, FaTerminal } from "react-icons/fa";

const NoCodeEditorSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState("dev-assistant");
  const [toolPermissions, setToolPermissions] = useState({
    github: "read-write",
    aws: "read-only",
    jira: "read-write"
  });
  
  // Auto-advance the demo every few seconds
  useEffect(() => {
    if (!inView) return;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => (prev + 1) % 5);
        setIsAnimating(false);
      }, 500);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [inView]);

  const handlePermissionChange = (tool: string, permission: string) => {
    setToolPermissions(prev => ({
      ...prev,
      [tool]: permission
    }));
  };

  // Available agent recipes
  const recipes = [
    {
      id: "dev-assistant",
      name: "Software Development Assistant",
      description: "A specialized agent that helps with code reviews, PR management, and deployment monitoring.",
      icon: FaCode({ size: 28 }),
      tools: ["GitHub", "AWS", "Jira"],
      color: "bg-blue-600"
    },
    {
      id: "customer-service",
      name: "Customer Service Agent",
      description: "Handles customer inquiries, processes returns, and manages support tickets.",
      icon: FaUserTie({ size: 28 }),
      tools: ["Zendesk", "Salesforce", "Intercom"],
      color: "bg-green-600"
    },
    {
      id: "data-analyst",
      name: "Data Analysis Agent",
      description: "Processes data requests, generates reports, and monitors data pipelines.",
      icon: FaRobot({ size: 28 }),
      tools: ["Tableau", "Snowflake", "Google Analytics"],
      color: "bg-purple-600"
    }
  ];

  // The steps in the no-code editor workflow
  const steps = [
    {
      title: "Select Agent Recipe",
      description: "Choose from pre-built agent templates designed for specific use cases.",
      highlightElements: ['recipe-cards', 'dev-assistant-card']
    },
    {
      title: "Create Your Agent",
      description: "Define your development assistant's personality, capabilities, and knowledge base.",
      highlightElements: ['agent-profile', 'agent-name', 'agent-capabilities']
    },
    {
      title: "Connect Intake Channels",
      description: "Link your development assistant to communication channels like Slack, Discord, or Email.",
      highlightElements: ['intake-slack', 'intake-gmail', 'intake-discord']
    },
    {
      title: "Configure Tool Access",
      description: "Set up connections to essential development tools with appropriate permission levels.",
      highlightElements: ['tool-github', 'tool-aws', 'tool-jira']
    },
    {
      title: "Validate & Deploy",
      description: "Verify tool connections with test commands and deploy your development assistant.",
      highlightElements: ['validation-panel', 'deploy-button', 'metrics-panel']
    }
  ];
  
  // Permission Toggle Switch Component
  const PermissionToggleSwitch = ({ tool, value }: { tool: string, value: string }) => {
    return (
      <div className="mt-2 flex space-x-2">
        <button
          onClick={() => handlePermissionChange(tool, 'read-only')}
          className={`flex items-center justify-center px-4 py-2 rounded-md text-xs font-medium transition-colors duration-200 ${
            value === 'read-only' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-200 text-gray-600'
          }`}
        >
          {FaLock({ className: "mr-1", size: 12 })}
          Read Only
        </button>
        
        <button
          onClick={() => handlePermissionChange(tool, 'read-write')}
          className={`flex items-center justify-center px-4 py-2 rounded-md text-xs font-medium transition-colors duration-200 ${
            value === 'read-write' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-200 text-gray-600'
          }`}
        >
          {FaLockOpen({ className: "mr-1", size: 12 })}
          Read & Write
        </button>
      </div>
    );
  };
  
  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            No-Code Platform
          </motion.span>
          <motion.h2 
            className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Build AI Agents Without Code
          </motion.h2>
          <motion.p 
            className="mt-4 text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Create, customize, and deploy powerful development assistants in minutes with our intuitive editor interface.
            No technical expertise required.
          </motion.p>
        </div>

        <div 
          ref={ref}
          className="relative"
        >
          {/* Editor UI mockup */}
          <motion.div 
            className="relative max-w-5xl mx-auto rounded-xl shadow-2xl overflow-hidden bg-white"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, type: "spring" }}
          >
            {/* Header toolbar */}
            <div className="bg-gray-800 h-14 px-4 flex items-center">
              <div className="flex space-x-2">
                <div className="h-3 w-3 rounded-full bg-red-500"></div>
                <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
              </div>
              <div className="ml-4 text-white text-sm font-medium">Agent Builder</div>
              <div className="ml-auto flex space-x-3">
                <div className="h-8 w-24 bg-gray-700 rounded-md"></div>
                <div className="h-8 w-8 bg-gray-700 rounded-md"></div>
              </div>
            </div>

            {/* Main editor content */}
            <div className="flex min-h-[650px]">
              {/* Sidebar navigation */}
              <div className="w-56 bg-gray-100 border-r border-gray-200 py-4 px-2">
                <div className="h-8 w-40 bg-primary-100 rounded-md mb-6 mx-auto"></div>
                
                <div className="space-y-1">
                  <div className={`flex items-center px-2 py-2 rounded-md relative ${
                    currentStep === 0 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}>
                    {currentStep === 0 && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-md"></div>}
                    <div className={`h-5 w-5 rounded-full ${
                      currentStep === 0 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    } flex items-center justify-center mr-2`}>
                      <span className="text-xs font-medium">1</span>
                    </div>
                    <div className="text-sm">Recipe Selection</div>
                  </div>
                  
                  <div className={`flex items-center px-2 py-2 rounded-md relative ${
                    currentStep === 1 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}>
                    {currentStep === 1 && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-md"></div>}
                    <div className={`h-5 w-5 rounded-full ${
                      currentStep === 1 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    } flex items-center justify-center mr-2`}>
                      <span className="text-xs font-medium">2</span>
                    </div>
                    <div className="text-sm">Agent Profile</div>
                  </div>
                  
                  <div className={`flex items-center px-2 py-2 rounded-md relative ${
                    currentStep === 2 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}>
                    {currentStep === 2 && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-md"></div>}
                    <div className={`h-5 w-5 rounded-full ${
                      currentStep === 2 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    } flex items-center justify-center mr-2`}>
                      <span className="text-xs font-medium">3</span>
                    </div>
                    <div className="text-sm">Intake Channels</div>
                  </div>
                  
                  <div className={`flex items-center px-2 py-2 rounded-md relative ${
                    currentStep === 3 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}>
                    {currentStep === 3 && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-md"></div>}
                    <div className={`h-5 w-5 rounded-full ${
                      currentStep === 3 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    } flex items-center justify-center mr-2`}>
                      <span className="text-xs font-medium">4</span>
                    </div>
                    <div className="text-sm">Tool Access</div>
                  </div>
                  
                  <div className={`flex items-center px-2 py-2 rounded-md relative ${
                    currentStep === 4 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}>
                    {currentStep === 4 && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-md"></div>}
                    <div className={`h-5 w-5 rounded-full ${
                      currentStep === 4 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    } flex items-center justify-center mr-2`}>
                      <span className="text-xs font-medium">5</span>
                    </div>
                    <div className="text-sm">Validate & Deploy</div>
                  </div>
                </div>
                
                <div className="mt-auto pt-6">
                  <div className="h-20 w-full bg-gray-200 rounded-md"></div>
                </div>
              </div>

              {/* Main content area */}
              <motion.div 
                className="flex-1 p-4 overflow-y-auto"
                animate={{ 
                  opacity: isAnimating ? 0.5 : 1,
                  scale: isAnimating ? 0.98 : 1
                }}
                transition={{ duration: 0.3 }}
                style={{ maxHeight: "calc(100vh - 200px)" }}
              >
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{steps[currentStep].title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{steps[currentStep].description}</p>
                </div>
                
                {/* Step 1: Recipe Selection */}
                {currentStep === 0 && (
                  <div className="space-y-3" id="recipe-cards">
                    {recipes.map((recipe) => (
                      <div 
                        key={recipe.id}
                        id={`${recipe.id}-card`}
                        className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                          selectedRecipe === recipe.id 
                            ? 'border-primary-500 bg-primary-50 shadow-md animate-pulse-subtle' 
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        onClick={() => setSelectedRecipe(recipe.id)}
                      >
                        <div className="flex items-start">
                          <div className={`${recipe.color} w-10 h-10 rounded-lg flex items-center justify-center text-white mr-3`}>
                            {recipe.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="text-base font-semibold text-gray-900">{recipe.name}</h4>
                              {selectedRecipe === recipe.id && (
                                <span className="text-primary-600">{FaCheck({ size: 16 })}</span>
                              )}
                            </div>
                            <p className="text-gray-600 text-xs mt-1">{recipe.description}</p>
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-500 mb-1">Recommended Integrations:</div>
                              <div className="flex space-x-2">
                                {recipe.tools.map((tool, i) => (
                                  <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                                    {tool}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="mt-1 text-xs text-gray-500">
                      <span className="font-medium">Pro tip:</span> Choose a recipe to get started quickly with pre-configured tool integrations and agent capabilities.
                    </div>
                  </div>
                )}
                
                {/* Step 2: Agent Creation */}
                {currentStep === 1 && (
                  <div className="space-y-3">
                    <div id="agent-name" className="bg-primary-50 animate-pulse-subtle p-3 rounded-lg">
                      <div className="font-medium text-xs text-gray-700 mb-1">Agent Name</div>
                      <div className="h-8 bg-white border border-gray-300 rounded-md flex items-center px-3 text-gray-400 text-sm">
                        DevAssist
                      </div>
                    </div>
                    
                    <div id="agent-profile" className="p-3 rounded-lg">
                      <div className="font-medium text-xs text-gray-700 mb-1">Description</div>
                      <div className="h-16 bg-white border border-gray-300 rounded-md p-2 text-xs text-gray-400">
                        A specialized development assistant that helps with code reviews, PR management, and deployment monitoring across GitHub, AWS, and Jira.
                      </div>
                    </div>
                    
                    <div id="agent-capabilities" className="bg-primary-50 animate-pulse-subtle p-3 rounded-lg">
                      <div className="font-medium text-xs text-gray-700 mb-1">Capabilities</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          "Code review and suggestions",
                          "PR status monitoring",
                          "AWS resource management",
                          "Jira ticket tracking",
                          "Deployment monitoring",
                          "Cost optimization suggestions"
                        ].map((capability, i) => (
                          <div key={i} className="flex items-center">
                            <div className="h-4 w-4 rounded bg-primary-500 border border-primary-600 mr-1 flex items-center justify-center text-white">
                              {FaCheck({ size: 8 })}
                            </div>
                            <div className="text-xs">{capability}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Intake Channels */}
                {currentStep === 2 && (
                  <div className="space-y-3">
                    <div className="font-medium text-xs text-gray-700 mb-1">Available Channels</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div id="intake-slack" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2 flex flex-col items-center">
                        <div className="w-8 h-8 bg-[#3F0E40] rounded-lg flex items-center justify-center text-white mb-1">
                          {SiSlack({ size: 20 })}
                        </div>
                        <div className="text-xs font-medium">Slack</div>
                        <div className="mt-1 h-6 w-16 bg-green-100 text-green-700 rounded-md flex items-center justify-center text-xs">Connected</div>
                      </div>
                      
                      <div id="intake-vscode" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2 flex flex-col items-center">
                        <div className="w-8 h-8 bg-[#007ACC] rounded-lg flex items-center justify-center text-white mb-1">
                          {FaCode({ size: 20 })}
                        </div>
                        <div className="text-xs font-medium">VS Code</div>
                        <div className="mt-1 h-6 w-16 bg-green-100 text-green-700 rounded-md flex items-center justify-center text-xs">Connected</div>
                      </div>
                      
                      <div id="intake-discord" className="border border-gray-200 rounded-lg p-2 flex flex-col items-center">
                        <div className="w-8 h-8 bg-[#5865F2] rounded-lg flex items-center justify-center text-white mb-1 opacity-50">
                          {SiDiscord({ size: 20 })}
                        </div>
                        <div className="text-xs font-medium text-gray-400">Discord</div>
                        <div className="mt-1 h-6 w-16 bg-gray-200 rounded-md flex items-center justify-center text-gray-500 text-xs">+ Add</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-2 rounded-lg mt-3">
                      <div className="font-medium text-xs text-gray-700 mb-1">Channel Notification Settings</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-1 border-b border-gray-200">
                          <div className="flex items-center">
                            <div className="h-4 w-4 rounded bg-primary-500 border border-primary-600 mr-1 flex items-center justify-center text-white">
                              {FaCheck({ size: 8 })}
                            </div>
                            <div className="text-xs">Notify on PR changes</div>
                          </div>
                          <div className="text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">Slack</div>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-gray-200">
                          <div className="flex items-center">
                            <div className="h-4 w-4 rounded bg-primary-500 border border-primary-600 mr-1 flex items-center justify-center text-white">
                              {FaCheck({ size: 8 })}
                            </div>
                            <div className="text-xs">Notify on deployment issues</div>
                          </div>
                          <div className="text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">Slack</div>
                        </div>
                        <div className="flex items-center justify-between py-1">
                          <div className="flex items-center">
                            <div className="h-4 w-4 rounded bg-primary-500 border border-primary-600 mr-1 flex items-center justify-center text-white">
                              {FaCheck({ size: 8 })}
                            </div>
                            <div className="text-xs">Code suggestions</div>
                          </div>
                          <div className="text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">VS Code</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 4: Tools */}
                {currentStep === 3 && (
                  <div className="space-y-3">
                    <div className="font-medium text-xs text-gray-700 mb-1">Development Tools & Permissions</div>
                    <div className="space-y-2">
                      <div id="tool-github" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center text-white mr-2">
                            {SiGithub({ size: 20 })}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium text-sm">GitHub</h4>
                              <div className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded">Connected</div>
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">Access PRs, issues, and repositories</p>
                            
                            <PermissionToggleSwitch tool="github" value={toolPermissions.github} />
                          </div>
                        </div>
                      </div>
                      
                      <div id="tool-aws" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-[#FF9900] rounded-lg flex items-center justify-center text-white mr-2">
                            {SiAmazon({ size: 20 })}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium text-sm">AWS</h4>
                              <div className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded">Connected</div>
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">Monitor resources, deployments, and costs</p>
                            
                            <PermissionToggleSwitch tool="aws" value={toolPermissions.aws} />
                          </div>
                        </div>
                      </div>
                      
                      <div id="tool-jira" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-[#0052CC] rounded-lg flex items-center justify-center text-white mr-2">
                            {SiJira({ size: 20 })}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium text-sm">Jira</h4>
                              <div className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded">Connected</div>
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">Manage tickets, track progress, and update statuses</p>
                            
                            <PermissionToggleSwitch tool="jira" value={toolPermissions.jira} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 5: Validate & Deploy */}
                {currentStep === 4 && (
                  <div className="space-y-3">
                    <div id="validation-panel" className="bg-primary-50 animate-pulse-subtle border border-gray-200 rounded-lg p-2">
                      <div className="font-medium text-xs text-gray-700 mb-1 flex items-center">
                        {FaRobot({ className: "mr-1" })}
                        AI Agent Tool Validation
                      </div>
                      <div className="bg-white border border-gray-200 text-gray-800 text-xs p-2 rounded-md h-32 overflow-y-auto">
                        <div className="mb-1.5 text-primary-600 font-medium">DevAssist: I've verified connections to all your tools. Here's what I can access:</div>
                        
                        <div className="mb-1.5 font-medium">GitHub:</div>
                        <div className="pl-2 mb-1.5">• <span className="text-blue-600">frontend-dashboard</span> - 3 open PRs including "Add dark mode support"</div>
                        <div className="pl-2 mb-1.5">• <span className="text-blue-600">api-service</span> - 2 open PRs, 5 issues assigned to your team</div>
                        <div className="pl-2 mb-2">• <span className="text-blue-600">documentation</span> - Last updated 2 days ago</div>
                        
                        <div className="mb-1.5 font-medium">AWS:</div>
                        <div className="pl-2 mb-1.5">• EC2: 4 instances running (2 production, 2 staging)</div>
                        <div className="pl-2 mb-1.5">• S3: 3 buckets with 756MB total storage used</div>
                        <div className="pl-2 mb-2">• Lambda: 5 functions with average execution time of 320ms</div>
                        
                        <div className="mb-1.5 font-medium">Jira:</div>
                        <div className="pl-2 mb-1.5">• <span className="text-purple-600">FE-452</span>: "Implement user settings page" (In Progress)</div>
                        <div className="pl-2 mb-1.5">• <span className="text-purple-600">BE-128</span>: "Fix API pagination bug" (Code Review)</div>
                        <div className="pl-2">• 8 tickets in current sprint, 3 marked as high priority</div>
                        
                        <div className="mt-2 text-primary-600 font-medium">All systems connected and working properly! I'm ready to assist with your development workflow.</div>
                      </div>
                    </div>
                    
                    <div id="deploy-button" className="bg-primary-50 animate-pulse-subtle p-3 rounded-lg flex flex-col items-center justify-center">
                      <div className="h-8 w-44 bg-primary-600 rounded-md flex items-center justify-center text-white font-medium text-sm cursor-pointer hover:bg-primary-700 transition-colors">
                        {FaArrowRight({ className: "mr-2", size: 12 })}
                        Deploy Assistant
                      </div>
                      <div className="text-xs text-gray-600 mt-2">Your assistant will be live immediately after deployment</div>
                    </div>
                    
                    <div id="metrics-panel" className="bg-gray-50 p-2 rounded-lg">
                      <div className="font-medium text-xs text-gray-700 mb-2">Estimated Performance</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-primary-50 animate-pulse-subtle p-2 rounded-lg">
                          <div className="text-xs text-gray-500">Response Time</div>
                          <div className="text-lg font-semibold">1.2s</div>
                          <div className="h-1.5 w-full bg-gray-200 rounded-full mt-1 overflow-hidden">
                            <div className="h-full w-3/4 bg-green-500 rounded-full"></div>
                          </div>
                        </div>
                        
                        <div className="bg-primary-50 animate-pulse-subtle p-2 rounded-lg">
                          <div className="text-xs text-gray-500">API Calls / Day</div>
                          <div className="text-lg font-semibold">~450</div>
                          <div className="h-1.5 w-full bg-gray-200 rounded-full mt-1 overflow-hidden">
                            <div className="h-full w-[60%] bg-yellow-500 rounded-full"></div>
                          </div>
                        </div>
                        
                        <div className="bg-primary-50 animate-pulse-subtle p-2 rounded-lg">
                          <div className="text-xs text-gray-500">Est. Cost</div>
                          <div className="text-lg font-semibold">Included in your plan</div>
                          <div className="h-1.5 w-full bg-gray-200 rounded-full mt-1 overflow-hidden">
                            <div className="h-full w-[25%] bg-green-500 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Bottom navigation */}
                <div className="mt-4 pt-2 border-t border-gray-200 flex justify-between">
                  <button 
                    className={`px-3 py-1 rounded-md border text-sm ${currentStep > 0 ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400'}`}
                    disabled={currentStep === 0}
                  >
                    Previous
                  </button>
                  <button 
                    className={`px-3 py-1 rounded-md text-sm ${currentStep < 4 ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-primary-600 text-white hover:bg-green-700'}`}
                  >
                    {currentStep < 4 ? 'Next' : 'Complete'}
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
          
          {/* Animated elements */}
          <motion.div 
            className="absolute -z-10 -right-20 top-20 h-64 w-64 rounded-full bg-primary-100 opacity-60"
            animate={{ 
              scale: [1, 1.1, 1],
              x: [0, 10, 0],
              y: [0, -15, 0]
            }}
            transition={{ 
              duration: 8,
              repeat: Infinity,
              type: "tween",
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute -z-10 -left-16 bottom-20 h-48 w-48 rounded-full bg-secondary-100 opacity-60"
            animate={{ 
              scale: [1, 1.15, 1],
              x: [0, -10, 0],
              y: [0, 10, 0]
            }}
            transition={{ 
              duration: 7,
              repeat: Infinity,
              type: "tween",
              ease: "easeInOut",
              delay: 0.5
            }}
          />
        </div>
        
        {/* Benefits cards */}
        <div className="mt-20 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Deploy in Minutes</h3>
              <p className="text-gray-600">
                From concept to live development assistant in minutes, not months. Our no-code interface 
                removes all technical barriers.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Secure Permissions</h3>
              <p className="text-gray-600">
                Granular control over what your agent can access. Set read-only or read-write permissions
                for each connected tool and service.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Developer Focused</h3>
              <p className="text-gray-600">
                Built specifically for software development workflows with integrations for GitHub, AWS, Jira,
                and popular development tools.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NoCodeEditorSection;